/**
 * KebabPopover — Floating popover action menu.
 *
 * Rendered at page level (NOT inside rows) to avoid overflow/stacking issues.
 * Anchored below-right of the kebab button via absolute positioning.
 * Status-aware: Active pieces show "Mark as sold", Sold pieces show "Undo sale".
 */

import React from 'react';
import { T } from '@/styles/tokens';

interface MenuItem {
  key: string;
  label: string;
  icon: string;
  showWhen?: 'active' | 'sold';
  dividerAbove?: boolean;
  destructive?: boolean;
}

const ALL_ITEMS: MenuItem[] = [
  { key: 'view',   label: 'View details',   icon: 'eye' },
  { key: 'adjust', label: 'Adjust inputs',  icon: 'edit' },
  { key: 'sold',   label: 'Mark as sold',   icon: 'markSold', showWhen: 'active', dividerAbove: true },
  { key: 'undo',   label: 'Undo sale',      icon: 'undo',     showWhen: 'sold',   dividerAbove: true },
  { key: 'delete', label: 'Delete artwork', icon: 'trash',    destructive: true,  dividerAbove: true },
];

function MenuIcon({ name, destructive }: { name: string; destructive?: boolean }) {
  const c = destructive ? '#C45A5A' : T.textSecondary;
  switch (name) {
    case 'eye':
      return (
        <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
          <circle cx="9" cy="9" r="6" stroke={c} strokeWidth="1.3" />
          <circle cx="9" cy="9" r="2.5" stroke={c} strokeWidth="1.3" />
        </svg>
      );
    case 'doc':
      return (
        <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
          <rect x="3.5" y="2" width="11" height="14" rx="1.5" stroke={c} strokeWidth="1.3" />
          <path d="M6.5 6h5M6.5 9h5M6.5 12h3" stroke={c} strokeWidth="1" strokeLinecap="round" />
        </svg>
      );
    case 'edit':
      return (
        <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
          <path d="M3 13.2V15h1.8L14 5.8 12.2 4 3 13.2z" stroke={c} strokeWidth="1.3" strokeLinejoin="round" />
        </svg>
      );
    case 'markSold':
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="5.5" stroke={T.primary} strokeWidth="1" opacity="0.35" />
          <circle cx="8" cy="8" r="2.5" fill={T.primary} />
        </svg>
      );
    case 'undo':
      return (
        <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
          <path d="M4 7h6.5a3.5 3.5 0 010 7H8" stroke={c} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M7 4L4 7l3 3" stroke={c} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'trash':
      return (
        <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
          <path d="M4.5 4.5h9M7 2.5h4M4 4.5l.75 10a1.5 1.5 0 001.5 1.5h5.5a1.5 1.5 0 001.5-1.5L14 4.5"
            stroke="#C45A5A" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    default:
      return null;
  }
}

export interface KebabPopoverProps {
  isSold: boolean;
  anchorX: number;
  anchorY: number;
  /** Viewport-relative bottom of the kebab button — used to decide flip */
  anchorBottom: number;
  onClose: () => void;
  onAction: (key: string) => void;
}

export function KebabPopover({ isSold, anchorX, anchorY, anchorBottom, onClose, onAction }: KebabPopoverProps) {
  const popRef = React.useRef<HTMLDivElement>(null);
  const [flipped, setFlipped] = React.useState(false);

  React.useEffect(() => {
    const el = popRef.current;
    if (!el) return;
    const popHeight = el.offsetHeight;
    const spaceBelow = window.innerHeight - anchorBottom;
    if (spaceBelow < popHeight + 8) setFlipped(true);
  }, [anchorBottom]);

  const visibleItems = ALL_ITEMS.filter(item => {
    if (item.showWhen === 'active') return !isSold;
    if (item.showWhen === 'sold') return isSold;
    return true;
  });

  return (
    <>
      {/* Invisible backdrop to catch outside clicks */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 99 }} />

      {/* Popover card */}
      <div ref={popRef} style={{
        position: 'fixed',
        ...(flipped
          ? { bottom: window.innerHeight - anchorY + 52 }
          : { top: anchorY }),
        left: anchorX,
        transform: 'translateX(-100%)',
        zIndex: 100,
        background: T.white,
        borderRadius: T.radiusMd,
        boxShadow: T.shadowFloating,
        padding: `${T.xs} 0`,
        minWidth: 188,
        animation: 'popIn 150ms cubic-bezier(0.22, 1, 0.36, 1)',
        transformOrigin: flipped ? 'bottom right' : 'top right',
      }}>
        {visibleItems.map(item => (
          <div key={item.key}>
            {item.dividerAbove && (
              <div style={{ height: 1, backgroundColor: T.border, margin: `${T.xs} 14px` }} />
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onAction(item.key); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', padding: '10px 14px',
                background: 'none', border: 'none', cursor: 'pointer',
                textAlign: 'left', fontSize: 14, fontWeight: 450,
                color: item.destructive ? '#C45A5A' : T.text,
                fontFamily: T.fontFamily,
              }}
            >
              <span style={{
                width: 20, display: 'flex', alignItems: 'center',
                justifyContent: 'center', flexShrink: 0,
              }}>
                <MenuIcon name={item.icon} destructive={item.destructive} />
              </span>
              {item.label}
            </button>
          </div>
        ))}
      </div>
    </>
  );
}
